import toast, { ToastOptions } from 'react-hot-toast'
import { RootState } from 'src/redux/root'
import { ubemIoApi } from 'src/redux/services/ubem'
import { TemplateMetadata } from 'src/redux/types'

import { Workflow } from '../../consts'
import { fileHandler } from '../../services/utils'
import { setCompletedWorkflow } from '../completion/reducers'
import { setError } from '../error/reducers'
import { Entrypoint } from './consts'
import {
  clearAllData,
  clearUserTemplateAssignments,
  loadGroupData,
  setAllKeyFields
} from './reducers'
import { UmibuilderState, UploadUserTemplateLibArg } from './types'
import { buildLegacyKeyFields, transformTemplateLib } from './utils'

const workflow = Workflow.umibuilder
const toastOptions = (
  endpoint: keyof typeof umibuilderApi.endpoints,
  additional?: Partial<ToastOptions>,
): ToastOptions => ({
  id: endpoint,
  position: 'bottom-center',
  ...additional,
  style: { minWidth: '30%' },
})

export const mutationFixedCacheKeys = {
  exportExchangeFile: 'exportExchangeFile',
  exportKeyFieldsErrorReport: 'exportKeyFieldsErrorReport',
  exportReferenceLibrary: 'exportReferenceLibrary',
  confirmKeyFields: 'confirmKeyFields',
  sendGroupData: 'sendGroupData',
  uploadUserTemplateLib: 'uploadUserTemplateLib',
}

export const umibuilderApi = ubemIoApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadBaseGisFile: builder.mutation({
      query: ({ body }) => ({
        url: 'api/umibuilder/upload',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          dispatch(clearAllData())
          const { data } = await queryFulfilled
          const state = getState() as RootState
          if (state.umibuilder.entrypoint == Entrypoint.uio) {
            dispatch(setAllKeyFields(data))
          }
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'uploadBaseGisFile' }))
        }
      },
    }),
    // TODO: abstract standardized file downloader into its own uniform endpoint which just takes a query arg
    exportExchangeFile: builder.mutation({
      query: ({ assignments, useLegacyNaming }) => {
        const body = useLegacyNaming ? buildLegacyKeyFields(assignments) : assignments
        return {
          url: 'api/umibuilder/export_exchange_file',
          method: 'POST',
          body,
          responseHandler: fileHandler,
        }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const res = await queryFulfilled

          const state = getState() as RootState
          const entrypoint = state.umibuilder.entrypoint
          dispatch(
            setCompletedWorkflow({
              context: entrypoint,
              workflow,
            }),
          )
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.exportExchangeFile,
            }),
          )
        }
      },
    }),
    exportKeyFieldsErrorReport: builder.mutation({
      query: ({ geoJSON }) => ({
        url: 'api/umibuilder/merge_errors',
        method: 'POST',
        body: geoJSON,
        responseHandler: fileHandler,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const res = await queryFulfilled
          toast.success('Donwloaded error report.')
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.exportKeyFieldsErrorReport,
            }),
          )
        }
      },
    }),
    confirmKeyFields: builder.mutation({
      query: ({ keyFields, fromCloud, project_id }) => ({
        url: 'api/umibuilder/create_group_data',
        method: 'POST',
        body: { keyFields, fromCloud, project_id },
      }),
      invalidatesTags: ['keyFields', 'Active Project', 'Scenarios'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(loadGroupData(data))
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.confirmKeyFields,
            }),
          )
        }
      },
    }),
    // TODO: make these group level for less rendering?
    sendGroupData: builder.mutation({
      query: (body) => ({
        url: 'api/umibuilder/send_group_data',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tempGroupData', 'Active Project', 'Scenarios'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.sendGroupData,
            }),
          )
        }
      },
    }),
    getBuildingCount: builder.query({
      query: (body) => ({
        url: 'api/umibuilder/get_building_count',
        method: 'POST',
        body,
      }),
      providesTags: ['tempGroupData'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getBuildingCount' }))
        }
      },
    }),
    uploadUserTemplateLibUmibuilder: builder.mutation<TemplateMetadata[], UploadUserTemplateLibArg>(
      {
        query: ({ body }) => {
          return {
            url: 'api/umibuilder/upload_template_lib',
            body,
            method: 'POST',
          }
        },
        transformResponse: (library: TemplateMetadata[], _, { body }) =>
          transformTemplateLib({ library, isUser: true, fromCloud: body?.fromCloud, id: body?.id }),
        onQueryStarted: async ({ body }, { dispatch, queryFulfilled, getState }) => {
          try {
            const { data } = await queryFulfilled
            const { fromCloud, id } = body
            dispatch(clearUserTemplateAssignments({ fromCloud, id }))
          } catch ({ error }) {
            dispatch(
              setError({
                error,
                workflow: 'all',
                key: mutationFixedCacheKeys.uploadUserTemplateLib,
              }),
            )
          }
        },
      },
    ),
    getUmiFile: builder.query({
      query: () => ({
        url: 'api/umibuilder/get_umi_export',
        responseHandler: fileHandler,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const res = await queryFulfilled
          const state = getState() as RootState
          const entrypoint = state.umibuilder.entrypoint
          dispatch(
            setCompletedWorkflow({
              context: entrypoint,
              workflow,
            }),
          )
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getUmiFile' }))
        }
      },
    }),
    getCZFromLatLon: builder.query({
      query: ({ lat, lon }) => ({
        url: `api/umibuilder/get_climate_zone?Lat=${lat}&Lon=${lon}`,
        method: 'GET',
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const res = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getCZFromLatLon' }))
        }
      },
    }),
    exportReferenceLibrary: builder.mutation({
      query: ({ templates, user }) => {
        return {
          url: 'api/umibuilder/build_template_library',
          method: 'POST',
          body: { templates, user },
          responseHandler: fileHandler,
        }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          toast.loading('Exporting template library...', toastOptions('exportReferenceLibrary'))
          const res = await queryFulfilled
          toast.success('Template library exported!', toastOptions('exportReferenceLibrary'))
        } catch ({ error }) {
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.exportReferenceLibrary,
            }),
          )
          toast.error('Error exporting template library', toastOptions('exportReferenceLibrary'))
        }
      },
    }),
  }),
})

export const {
  uploadBaseGisFile,
  exportExchangeFile,
  exportKeyFieldsErrorReport,
  confirmKeyFields,
  sendGroupData,
  getBuildingCount,
  uploadUserTemplateLibUmibuilder,
  getUmiFile,
  getCZFromLatLon,
  exportReferenceLibrary,
} = umibuilderApi.endpoints
